import { useCallback, useEffect, useRef, useState } from 'react';
import { usePolling } from '../../../../../utils/commonUtil';
import { getFinancialsIngestionStatus } from './service';
import { FINANCIALS_INGESTION_MAX_CONSECUTIVE_FAILURES, FINANCIALS_INGESTION_POLL_INTERVAL_MS } from './constants';
import { FinancialsIngestionStatus } from './types';

interface UseFinancialsIngestionStatusParams {
    orgId: string;
    fileType: string;
    /** false in PDF-report mode — no poll must ever fire there. */
    enabled: boolean;
    /** Fires exactly once on the true -> false transition, so the tab can refetch its table data. */
    onCompleted: () => void;
}

/**
 * Polls aica's financials ingestion-status endpoint at FINANCIALS_INGESTION_POLL_INTERVAL_MS so
 * the Audited/Provisional tabs can show a loader note instead of "(No data available)" while an
 * uploaded file is still being extracted (RECUR-28096).
 *
 * Three behaviours are load-bearing here, all worked around because the shared `usePolling`
 * (src/utils/commonUtil.ts) cannot be changed for this feature alone:
 *
 *  - `usePolling` has no `enabled` flag and polls once immediately on mount. `fetchData` returns
 *    `undefined` synchronously (before doing any async work) when disabled/orgId-less, and
 *    `shouldContinue` reports `false` in that case, so the interval it creates is cleared before
 *    it can ever tick — zero network calls in PDF mode.
 *  - `usePolling` stops permanently on the FIRST thrown error. `fetchData` therefore catches its
 *    own errors, tracks consecutive failures in a ref (a state var would re-render and restart
 *    the poll interval), and returns `null` — which `onSuccess` treats as a no-op so a transient
 *    failure never clears an already-showing note. `shouldContinue` keeps polling until
 *    FINANCIALS_INGESTION_MAX_CONSECUTIVE_FAILURES is reached; any successful read resets it.
 *  - Once that budget is exhausted, `usePolling` stops for good — and with it, our only way of
 *    ever finding out whether the document actually finished. Leaving `inProgress` at whatever it
 *    last held would hang the loader forever (the aica-side 30-minute bound cannot rescue this —
 *    it only applies on a read, and we have stopped reading). `onSuccess` therefore degrades to
 *    pre-feature behaviour instead: stop showing the loader. That is NOT a completion, so it
 *    deliberately bypasses the true -> false effect's `onCompleted` call (`gaveUpRef`) — no
 *    document finished, and firing the refetch `onCompleted` implies would misrepresent a failure
 *    as a success.
 */
export function useFinancialsIngestionStatus({ orgId, fileType, enabled, onCompleted }: UseFinancialsIngestionStatusParams) {
    const [inProgress, setInProgress] = useState(false);

    const failureCountRef = useRef(0);
    const previousInProgressRef = useRef(false);
    // Set right before the setInProgress(false) call that gives up on the failure budget (below),
    // so the completion effect can tell "the document finished" apart from "we stopped being able
    // to check" — both are true -> false transitions on `inProgress`, but only the first is a real
    // completion. Only ever set when a transition is actually about to happen, so a give-up with
    // nothing in flight (inProgress already false) can never linger and suppress a later, real
    // completion.
    const gaveUpRef = useRef(false);

    // Keep ref in sync with prop to avoid stale closure in the polled hook below (both tabs'
    // onCompleted closes over callables re-created every render).
    const onCompletedRef = useRef(onCompleted);
    useEffect(() => {
        onCompletedRef.current = onCompleted;
    }, [onCompleted]);

    const fetchData = useCallback(async (): Promise<FinancialsIngestionStatus | null | undefined> => {
        if (!enabled || !orgId) return undefined;
        try {
            const res = await getFinancialsIngestionStatus({ orgId, fileType });
            failureCountRef.current = 0;
            return res?.data?.data;
        } catch (err) {
            failureCountRef.current += 1;
            return null;
        }
    }, [enabled, orgId, fileType]);

    const onSuccess = useCallback((data: FinancialsIngestionStatus | null | undefined) => {
        // A failed poll (null) is normally a no-op — the note keeps its last known value rather
        // than being cleared by a transient read failure. `undefined` (disabled, or a
        // settled/malformed body) is a real "not in progress" answer, handled below.
        if (data === null) {
            // This is also the tick `shouldContinue` (below) uses to decide whether to stop
            // polling for good, so it is our last chance to react. See the "give up" bullet in the
            // hook's doc comment for why this degrades to pre-feature behaviour (stop the loader)
            // instead of leaving it stuck, and why that must NOT be treated as a completion.
            if (failureCountRef.current >= FINANCIALS_INGESTION_MAX_CONSECUTIVE_FAILURES) {
                setInProgress((prev) => {
                    if (prev) gaveUpRef.current = true;
                    return false;
                });
            }
            return;
        }
        setInProgress(!!data?.inProgress);
    }, []);

    const shouldContinue = useCallback(
        (data: FinancialsIngestionStatus | null | undefined) => {
            if (!enabled || !orgId) return false;
            if (data === null) return failureCountRef.current < FINANCIALS_INGESTION_MAX_CONSECUTIVE_FAILURES;
            return true;
        },
        [enabled, orgId],
    );

    usePolling<FinancialsIngestionStatus | null | undefined>({
        fetchData,
        onSuccess,
        shouldContinue,
        interval: FINANCIALS_INGESTION_POLL_INTERVAL_MS,
    });

    useEffect(() => {
        if (previousInProgressRef.current && !inProgress) {
            // A give-up (failure-budget exhausted) also flips inProgress true -> false, but it is
            // not a completion — the document never finished, we just stopped being able to check.
            // Consume the flag rather than firing onCompleted's refetch on what was really a failure.
            if (gaveUpRef.current) {
                gaveUpRef.current = false;
            } else {
                onCompletedRef.current();
            }
        }
        previousInProgressRef.current = inProgress;
    }, [inProgress]);

    return { inProgress };
}
